import {
  EmailAuthProvider,
  getAuth,
  getMultiFactorResolver,
  reauthenticateWithCredential,
  verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'sonner';
import React, { useState } from 'react';
import { Stack, TextField, Button } from '@mui/material';
import { captureException as sentryCaptureException } from '@sentry/react';

import { useAuth } from '../../../hooks/useAuth';
import { MFA_ERROR_CODES } from '../constants';
import { MFAVerificationDialogue } from './MFAVerificationDialogue';
import { functions } from '../../../firebase-config';

const checkEmailAvailabilityFn = httpsCallable(functions, 'checkEmailAvailability_v2');

/**
 * @param {Object} props
 * @param {string} props.existingEmail
 * @param {string | null | undefined} [props.pendingEmail]
 * @param {boolean} props.isFormView
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setIsFormView
 * @param {boolean} [props.isResendEmailPage]
 * @returns
 */
export const UpdateEmail = ({ existingEmail, pendingEmail, isFormView, setIsFormView, isResendEmailPage = false }) => {
  const { userType, setEmailVerificationDetails } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const [existingPassword, setExistingPassword] = useState('');
  const [newEmail, setNewEmail] = useState(pendingEmail || '');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [mfaResolver, setMfaResolver] = useState(
    /** @type {import('@firebase/auth').MultiFactorResolver | null} */ (null),
  );

  const handleEmailChange = (value) => {
    setEmailError('');
    const cleanseEmail = value.trim().toLowerCase();
    setNewEmail(cleanseEmail);
  };

  const handleExistingPasswordChange = (value) => {
    setPasswordError('');
    setExistingPassword(value);
  };

  const cancelEmailUpdate = () => {
    setIsFormView(false);
    setExistingPassword('');
    setNewEmail('');
    setPasswordError('');
    setEmailError('');
  };

  const handleUpdateEmail = async (isPostMFA = false) => {
    if (!newEmail) {
      setEmailError('This field is required');

      return;
    }

    if (existingEmail === newEmail && !isResendEmailPage) {
      setEmailError('Email already in use');

      return;
    }

    if (!existingPassword) {
      setPasswordError('This field is required');

      return;
    }

    const auth = getAuth();

    try {
      const { currentUser } = auth;

      if (!currentUser) {
        toast.error('You need to be logged in to update your email');

        return;
      }

      setIsLoading(true);

      // Check that the new email is not already registered to a different account.
      // Rate-limited to 10 requests/min per user via a Firestore fixed window on the backend.
      try {
        const { data: availabilityData } = await checkEmailAvailabilityFn({ email: newEmail });
        if (availabilityData?.available === false) {
          setEmailError('This email address is already in use by another account');
          setIsLoading(false);

          return;
        }
      } catch (availabilityCheckError) {
        if (availabilityCheckError?.code === 'functions/resource-exhausted') {
          setEmailError('Too many requests. Please try again later.');
          setIsLoading(false);

          return;
        }

        // For any other failure (network, cold-start, etc.) log to Sentry but fall through —
        // the server-side guard in updateUserEmail will still block duplicate emails.
        sentryCaptureException(availabilityCheckError, { extra: { userType } });
      }

      if (!isPostMFA) {
        const credential = EmailAuthProvider.credential(existingEmail, existingPassword);
        await reauthenticateWithCredential(currentUser, credential);
      }

      await verifyBeforeUpdateEmail(currentUser, newEmail)
        .then(async () => {
          // This is where email verification details are set on authcontext after sending an email verification
          setEmailVerificationDetails((previousState) => ({
            ...previousState,
            oldEmail: existingEmail,
            newEmail,
            isEmailVerified: false,
          }));
          toast.success('Successfully updated account email', {
            description: `Your account email has been updated to ${newEmail}.`,
          });
        })
        .catch((error) => {
          sentryCaptureException(error, { extra: { errorCode: error.code, userType } });

          toast.error('An error occurred, please try again', {
            description: `${error.code}: ${error.message}`,
          });
        });
    } catch (err) {
      // NOTE: TODO: We may not want to capture this as an exception, but rather as a message.
      // Will find out how often this fires.
      sentryCaptureException(err, { extra: { errorCode: err.code, userType } });

      switch (err.code) {
        case MFA_ERROR_CODES.INVALID_EMAIL:
          setEmailError(err.message);
          break;
        case MFA_ERROR_CODES.MULTI_FACTOR_AUTH_REQUIRED:
          setMfaResolver(getMultiFactorResolver(auth, /** @type {import('@firebase/auth').MultiFactorError} */ (err)));
          break;
        case MFA_ERROR_CODES.EMAIL_ALREADY_IN_USE:
          setEmailError(err.message);
          break;
        case MFA_ERROR_CODES.ARGUMENT_ERROR:
        case MFA_ERROR_CODES.WRONG_PASSWORD:
          setPasswordError('Please check your password and try again');
          break;
        default:
          toast.error('An error occurred', {
            description: 'Please try again, or contact support if the problem persists.',
          });
          break;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnSuccess = () => {
    setMfaResolver(null);
    handleUpdateEmail(true);
  };

  return (
    <Stack direction="column" gap={2} width="100%">
      {!isResendEmailPage && (
        <TextField label="Login Email" value={existingEmail || ''} variant="outlined" disabled fullWidth />
      )}
      {isFormView && (
        <>
          <TextField
            fullWidth
            disabled={isResendEmailPage}
            label="New Login Email"
            autoComplete="new-email"
            value={newEmail || ''}
            onChange={(e) => handleEmailChange(e.target.value)}
            variant="outlined"
            error={emailError !== ''}
            helperText={emailError}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            autoComplete="current-password"
            value={existingPassword || ''}
            onChange={(e) => handleExistingPasswordChange(e.target.value)}
            variant="outlined"
            error={passwordError !== ''}
            helperText={passwordError}
          />

          <Stack direction="row" gap={2} alignItems="center" justifyContent="center">
            <Button variant="contained" color="primary" onClick={() => handleUpdateEmail(false)} loading={isLoading}>
              {isResendEmailPage ? 'Resend' : 'Update'}
            </Button>
            <Button variant="text" color="primary" onClick={cancelEmailUpdate} disabled={isLoading}>
              Cancel
            </Button>
          </Stack>
        </>
      )}
      {!isFormView && (
        <Button variant="text" color="primary" onClick={() => setIsFormView(true)}>
          Change Login Email
        </Button>
      )}

      <MFAVerificationDialogue mfaResolver={mfaResolver} setMfaResolver={setMfaResolver} onSuccess={handleOnSuccess} />
    </Stack>
  );
};
